// Google Tag Manager. It shares the Meta Pixel's consent: the container is not loaded
// until the person has said yes, and useMetaPixel's switches drive the ones here.
// A loaded container cannot be unloaded. A later no, a pause or a minor tells its tags
// that consent is denied and stops the hub's own pushes, but tags that ignore consent
// keep running until the page reloads.

// Only these app events reach the container, and only with the sponsor its triggers read.
const GTM_EVENTS = new Set(['Client Verified', 'Payment Success']);

const CONSENT_KEYS = ['ad_storage', 'ad_user_data', 'ad_personalization', 'analytics_storage'] as const;

// Events from a page whose choice is not known yet: a sign-in or a return from
// checkout fires its conversion before the account's answer arrives.
const HELD_LIMIT = 10;

let allowed = false;
let paused = false;
let minorStopped = false;
let refused = false;
let loaded = false;
let denied = false;
let held: Record<string, string>[] = [];

export const normaliseGtmId = (value: unknown): string => {
   const id = typeof value === 'string' ? value.trim().toUpperCase() : '';
   return /^GTM-[A-Z0-9]{4,12}$/.test(id) ? id : '';
};

const gtmId = () => {
   try {
      return normaliseGtmId(useRuntimeConfig().public.GTM_ID);
   } catch {
      return '';
   }
};

const isActive = () => loaded && allowed && !paused && !minorStopped;

const dataLayer = (): unknown[] => {
   const w = window as any;
   w.dataLayer = w.dataLayer || [];
   return w.dataLayer;
};

// gtag must push the arguments object itself: GTM reads consent commands only in that form.
function gtag(..._args: unknown[]) {
   // eslint-disable-next-line prefer-rest-params
   dataLayer().push(arguments);
}

const consent = (state: 'granted' | 'denied') =>
   Object.fromEntries(CONSENT_KEYS.map((key) => [key, state]));

const load = (id: string) => {
   loaded = true;
   try {
      // The default must be in the data layer before the container reads it.
      gtag('consent', 'default', consent('granted'));
      dataLayer().push({ 'gtm.start': Date.now(), event: 'gtm.js' });
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(id)}`;
      (document.head || document.documentElement).appendChild(script);
   } catch {
      // A third-party script must never break the page.
   }
};

const deny = () => {
   if (!loaded || denied) return;
   denied = true;
   try {
      gtag('consent', 'update', consent('denied'));
   } catch {
      // See load.
   }
};

const flushHeld = () => {
   const waiting = held;
   held = [];
   if (!isActive()) return;
   try {
      for (const entry of waiting) dataLayer().push(entry);
   } catch {
      // See load.
   }
};

// Called by setPixelAllowed with its own verdict, so a blank pixel id or a minor
// keeps the container off too.
export const setGtmAllowed = (next: boolean) => {
   paused = false;
   const id = gtmId();
   if (next && id && !minorStopped) {
      allowed = true;
      refused = false;
      if (!loaded) load(id);
      else if (denied) {
         denied = false;
         try {
            gtag('consent', 'update', consent('granted'));
         } catch {
            // See load.
         }
      }
      flushHeld();
      return;
   }
   allowed = false;
   refused = true;
   held = [];
   deny();
};

// A different account signed in and its choice is not known yet.
export const pauseGtm = (dropHeld = false) => {
   paused = true;
   refused = false;
   if (dropHeld) held = [];
   deny();
};

export const stopGtmForMinor = () => {
   minorStopped = true;
   allowed = false;
   held = [];
   deny();
};

// One allow-listed app event. Returns whether it was pushed now; an event whose
// choice is still pending is held and pushed after a yes.
export const pushGtmEvent = (event: string, properties?: Record<string, unknown>): boolean => {
   if (!GTM_EVENTS.has(event) || minorStopped) return false;
   const entry: Record<string, string> = { event };
   if (typeof properties?.sponsor === 'string') entry.sponsor = properties.sponsor;
   if (!isActive()) {
      if (!refused && held.length < HELD_LIMIT) held.push(entry);
      return false;
   }
   try {
      dataLayer().push(entry);
      return true;
   } catch {
      return false;
   }
};
